<template>
  <data-card class="entry-export-data-container" :title="title">
    <v-layout class="white" row wrap>
      <v-flex xs12 v-if="isLoading">
        <v-progress-linear indeterminate class="my-0"></v-progress-linear>
      </v-flex>
      <v-flex xs12>
        <v-container fluid grid-list-lg pa-3>
          <v-form
            ref="formEntryExportDetails"
            id="formEntryExportDetails"
            v-model="isFormValid"
            lazy-validation
            @submit.prevent="onSubmit">
            <v-layout row wrap>
              <v-flex xs12 md4>
                <date-time-picker
                  v-model="item.exportFrom"
                  :text-field-label="$t('administration.entryExportDetails.from')"
                  :text-field-placeholder="$t('administration.entryExportDetails.from')"
                  text-field-prepend-icon="event"
                  time-picker-format="24hr"
                  :date-picker-locale="$i18n.locale"
                  icon-color="primary"
                  :text-field-rules="rules"
                  is-date-only
                  :disabled="!isNew"
                  :text-field-readonly="!isNew"
                ></date-time-picker>
              </v-flex>
              <v-flex xs12 md4>
                <date-time-picker
                  v-model="item.exportTo"
                  :text-field-label="$t('administration.entryExportDetails.to')"
                  :text-field-placeholder="$t('administration.entryExportDetails.to')"
                  text-field-prepend-icon="event"
                  time-picker-format="24hr"
                  :date-picker-locale="$i18n.locale"
                  icon-color="primary"
                  :text-field-rules="rules"
                  is-date-only
                  :disabled="!isNew"
                  :text-field-readonly="!isNew"
                ></date-time-picker>
              </v-flex>
              <v-flex xs12 md3>
                <year-picker
                  v-model="item.bookingYear"
                  :text-field-label="$t('administration.entryExportDetails.bookingYear')"
                  :date-picker-first-day-of-week="1"
                  :date-picker-locale="$i18n.locale"
                  :text-field-rules="rules"
                  :disabled="!isNew"
                  :text-field-readonly="!isNew"
                  >
                </year-picker>
              </v-flex>
              <v-flex xs12 md1 lg1>
                <v-checkbox
                      v-model="item.isTestExport"
                      :label="$t('administration.entryExportDetails.isTestExport')"
                      hide-details
                      :readonly="!isNew"
                    ></v-checkbox>
              </v-flex>
            </v-layout>
            <v-layout row wrap v-if="!isNew">
              <v-flex xs12 md4>
                <v-text-field
                    v-model="item.author"
                    :label="$t('administration.entryExportDetails.author')"
                    :placeholder="$t('administration.entryExportDetails.author')"
                    readonly=""
                ></v-text-field>
              </v-flex>
              <v-flex xs12 md4>
                <date-time-picker
                  v-model="item.exportDate"
                  :text-field-label="$t('administration.entryExportDetails.date')"
                  :text-field-placeholder="$t('administration.entryExportDetails.date')"
                  text-field-prepend-icon="event"
                  time-picker-format="24hr"
                  :date-picker-locale="$i18n.locale"
                  icon-color="primary"
                  :text-field-rules="rules"
                  disabled
                  text-field-readonly
                ></date-time-picker>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs12>
                <v-textarea
                    v-model="item.data"
                    :label="$t('administration.entryExportDetails.content')"
                    :placeholder="$t('administration.entryExportDetails.content')"
                    readonly
                    :rows="20"
                ></v-textarea>
              </v-flex>
            </v-layout>
            <v-layout justify-end row>
              <v-btn
                v-if="this.isNew"
                @click="loadPreview"
              >
                {{$t('administration.entryExportDetails.preview')}}
                <v-icon right color="secondary">slideshow</v-icon>
              </v-btn>
              <v-btn
                v-if="!this.isNew"
                :href="downloadDataUrl"
                target="_blank"
              >
                {{$t('administration.entryExportDetails.download')}}
                <v-icon right color="secondary">get_app</v-icon>
              </v-btn>
              <v-btn
                v-if="this.isNew && this.isUserAdministrator"
                type="submit"
                form="formEntryExportDetails"
                :loading="isSaving"
                :disabled="!isFormValid || !this.canSave"
              >
                {{$t('common.save')}}
                <v-icon right color="primary">save</v-icon>
              </v-btn>
            </v-layout>
          </v-form>
        </v-container>
      </v-flex>
    </v-layout>
  </data-card>
</template>

<script>
import formRuleHelper from '@/scripts/formRuleHelper'
import { mapGetters } from 'vuex'
import { Api } from '@/api/apiAccess'
import Routes from '@/router/routes'
import { snackBarMixin, confirmMixin } from '@/mixins/mixins'
import mutationtypes from '@/store/modules/entryexport/mutation-types'
import moduleNames from '@/store/moduleNames'

export default {
  props: {
    type: {
      required: true
    },
    id: {
      required: true
    }
  },
  mixins: [
    snackBarMixin,
    confirmMixin
  ],
  data () {
    return {
      isLoading: false,
      item: {},
      isFormValid: true,
      canSave: true,
      isSaving: false,
      rules: [
        formRuleHelper.required(this.$t('common.valueRequired'))
      ]
    }
  },
  computed: {
    ...mapGetters(['isUserAdministrator']),
    title () {
      let type = Number(this.type)
      if (type === 0) {
        return this.$t('administration.entryExportDetails.pettyCashEntryExportTitle')
      } else if (type === 1) {
        return this.$t('administration.entryExportDetails.transferExportTitle')
      }
      return null
    },
    isNew () {
      return this.id <= 0
    },
    downloadDataUrl () {
      let paramCreator = Api.EntryExportParamCreator
      if (this.isNew) {
        return null
      }
      let type = this.item.exportType
      let culture = this.$i18n.locale
      let params = {}
      if (type === 0) {
        params = paramCreator.entryExportDownloadPettyCashEntryExportData(this.item.id, culture)
      } else if (type === 1) {
        params = paramCreator.entryExportDownloadTransferExportData(this.item.id, culture)
      }
      let relativeUrl = params.url
      let basePath = Api.BasePath

      if (relativeUrl == null || relativeUrl.length <= 0 || basePath == null || basePath.length <= 0) {
        return null
      }

      return basePath + relativeUrl
    }
  },
  methods: {
    loadItem () {
      this.isLoading = true
      let ep = Api.EntryExport
      let apiCall = null
      let type = Number(this.type)
      if (type === 0) {
        apiCall = ep.entryExportGetPettyCashEntryExport(this.id)
      } else if (type === 1) {
        apiCall = ep.entryExportGetTransferExport(this.id)
      }
      apiCall.then(
        r => {
          this.item = r.data
        }
      ).finally(() => {
        this.isLoading = false
      })
    },
    validateForm () {
      return this.$refs.formEntryExportDetails.validate()
    },
    create () {
      this.canSave = false
      this.isSaving = true

      let culture = this.$i18n.locale
      let ep = Api.EntryExport
      ep.entryExportPost(this.item, culture)
        .then(r => {
          this.showStatusMessage(this.$t('common.dataSaved'))
          this.$store.commit(moduleNames.ENTRYEXPORT + '/' + mutationtypes.UPDATE_EXPORTLIST_SELECTED_ITEM, { item: r.data })
          let editRoute = { name: Routes.ENTRY_EXPORT_DETAILS, params: { type: r.data.exportType, id: r.data.id } }
          this.$router.replace(editRoute)
        })
        .finally(() => {
          this.isSaving = false
          this.canSave = true
        })
    },
    save () {
      this.create()
    },
    onSubmit () {
      if (this.validateForm()) {
        this.save()
      }
    },
    loadPreview () {
      if (!this.isNew) {
        return
      }
      let ep = Api.EntryExport
      let apiCall = null
      let type = Number(this.type)
      let exportFrom = new Date(this.item.exportFrom)
      let exportTo = new Date(this.item.exportTo)
      let bookingYear = this.item.bookingYear
      let isTextExport = this.item.isTestExport
      let culture = this.$i18n.locale
      if (type === 0) {
        apiCall = ep.entryExportPreviewPettyCashEntryExport(exportFrom, exportTo, bookingYear, isTextExport, culture)
      } else if (type === 1) {
        apiCall = ep.entryExportPreviewTransferExport(exportFrom, exportTo, bookingYear, isTextExport, culture)
      }
      apiCall.then(
        r => {
          this.item.data = r.data
        }
      ).finally(() => {
        this.isLoading = false
      })
    },
    init () {
      let now = new Date()
      let from = new Date(now.getFullYear(), now.getMonth(), 1)
      let to = new Date(now.getFullYear(), now.getMonth() + 1, 1)

      this.item = {
        exportFrom: from.toISOString(),
        exportTo: to.toISOString(),
        bookingYear: now.getFullYear(),
        data: null,
        isTestExport: false,
        exportType: this.type
      }
    }
  },
  created () {
    if (this.id > 0) {
      this.loadItem()
    } else {
      this.init()
    }
  }
}
</script>

<style lang="scss">
.entry-export-data-container {

  .v-textarea {

    textarea {
      white-space: pre;
    }
  }
}
</style>
